import { Box, Button, Checkbox, Flex, FormControl, Icon, Input, InputGroup, InputLeftElement, Radio, RadioGroup, Spinner, Stack, Text } from "@chakra-ui/react";
import { debounce } from "lodash";
import { type ChangeEvent, type RefObject, type UIEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSearch } from "react-icons/fa";
import { SelectorValue } from "../input/Selector/SelectorValue";
import { TextLengthHint } from "../input/TextLengthHint";
import { SEARCH_MAX_LENGTH, VALUE_SEARCH_FROM_OPTION_COUNT } from "./columnFilters";
import type { EntityTableColumn } from "./EntityTable";
import { keepFocusIn, moveBetweenControls } from "./menuFocus";
import { NumberRangeFilter } from "./NumberRangeFilter";

export const ColumnSearchEditor = ({ column, onClose }: { column: EntityTableColumn; onClose: () => void }) => (
  <Box onKeyDown={moveBetweenControls(onClose)}>
    <TextSearch column={column} />
  </Box>
);

export const ColumnValueEditor = ({
  column,
  debounceMs = 300,
  closings = 0,
  onClose,
}: {
  column: EntityTableColumn;
  debounceMs?: number;
  closings?: number;
  onClose: () => void;
}) => {
  const area = useRef<HTMLDivElement>(null);
  return (
    <Box ref={area} onKeyDown={moveBetweenControls(onClose)}>
      {column.multiSelectConfig && (
        <MultiSelect
          column={column}
          config={column.multiSelectConfig}
          debounceMs={debounceMs}
          closings={closings}
          area={area}
        />
      )}
      {column.singleSelectConfig && (
        <SingleSelect config={column.singleSelectConfig} debounceMs={debounceMs} closings={closings} area={area} />
      )}
      {column.dateRangeConfig && <DateRange config={column.dateRangeConfig} />}
      {column.numberRangeConfig && (
        <NumberRangeFilter columnLabel={column.label} config={column.numberRangeConfig} debounceMs={debounceMs} />
      )}
    </Box>
  );
};

type Area = RefObject<HTMLDivElement>;
type MultiSelectConfig = NonNullable<EntityTableColumn["multiSelectConfig"]>;
type SingleSelectConfig = NonNullable<EntityTableColumn["singleSelectConfig"]>;
type DateRangeConfig = NonNullable<EntityTableColumn["dateRangeConfig"]>;

const TextSearch = ({ column }: { column: EntityTableColumn }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState(column.currentSearchValue || "");

  useEffect(() => {
    setSearchTerm(column.currentSearchValue || "");
  }, [column.currentSearchValue]);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    column.onSearch?.(e.target.value);
  };

  return (
    <FormControl px={3} py={2}>
      <InputGroup size="sm">
        <InputLeftElement>
          <Icon as={FaSearch} color="smMuted" />
        </InputLeftElement>
        <Input
          placeholder={`${t("Search")}...`}
          aria-label={t("Search in {{column}}", { column: column.label }) ?? ""}
          value={searchTerm}
          onChange={handleSearchChange}
          maxLength={SEARCH_MAX_LENGTH}
        />
      </InputGroup>
      <TextLengthHint text={searchTerm} max={SEARCH_MAX_LENGTH} />
    </FormControl>
  );
};

const useOptionSearch = (
  options: SelectorValue[],
  onInputChange: ((input: string) => void | Promise<void>) | undefined,
  debounceMs: number,
  closings: number,
) => {
  const [term, setTerm] = useState("");
  useEffect(() => setTerm(""), [closings]);
  const latestOnInputChange = useRef(onInputChange);
  latestOnInputChange.current = onInputChange;
  const searchOnServer = useMemo(
    () => debounce((value: string) => void latestOnInputChange.current?.(value), debounceMs),
    [debounceMs],
  );
  useEffect(() => () => searchOnServer.cancel(), [searchOnServer]);

  const searchesOnServer = !!onInputChange;
  const change = (e: ChangeEvent<HTMLInputElement>) => {
    setTerm(e.target.value);
    if (searchesOnServer) searchOnServer(e.target.value);
  };
  const matching = useMemo(() => {
    const wanted = term.trim().toLowerCase();
    if (searchesOnServer || !wanted) return options;
    return options.filter((option) => option.label.toLowerCase().includes(wanted));
  }, [options, searchesOnServer, term]);

  return {
    term,
    change,
    matching,
    isShown: searchesOnServer || options.length >= VALUE_SEARCH_FROM_OPTION_COUNT,
  };
};

const NEAR_LIST_END_PX = 24;

const useLoadMoreNearEnd = ({
  options,
  optionsCount,
  isLoading,
  isError,
  onLoadMore,
  onRetry,
}: MultiSelectConfig | SingleSelectConfig) => {
  const askedFor = useRef<SelectorValue[] | null>(null);
  const mayHaveMore = optionsCount == null || options.length < optionsCount;
  const fetchNext = isError ? (onRetry ?? onLoadMore) : onLoadMore;
  return (e: UIEvent<HTMLDivElement>) => {
    const list = e.currentTarget;
    if (!fetchNext || !mayHaveMore || isLoading) return;
    if (!isError && askedFor.current === options) return;
    if (list.scrollTop + list.clientHeight < list.scrollHeight - NEAR_LIST_END_PX) return;
    askedFor.current = options;
    fetchNext();
  };
};

const OptionsLoadError = ({ onRetry }: { onRetry?: () => void }) => {
  const { t } = useTranslation();
  return (
    <Flex align="center" justifyContent="center" gap="2" wrap="wrap" pt="1">
      <Text fontSize="sm" color="smError" textTransform="none" fontWeight="normal">
        {t("Could not load the filter choices. Try again.")}
      </Text>
      {onRetry && (
        <Button size="sm" variant="link" colorScheme="teal" onClick={onRetry}>
          {t("Try again")}
        </Button>
      )}
    </Flex>
  );
};

const OptionSearch = ({ term, onChange }: { term: string; onChange: (e: ChangeEvent<HTMLInputElement>) => void }) => {
  const { t } = useTranslation();
  return (
    <InputGroup size="sm" mb={2}>
      <InputLeftElement>
        <Icon as={FaSearch} color="smMuted" />
      </InputLeftElement>
      <Input
        placeholder={`${t("Search options")}...`}
        aria-label={t("Search options") ?? ""}
        value={term}
        onChange={onChange}
      />
    </InputGroup>
  );
};

const MultiSelect = ({
  column,
  config,
  debounceMs,
  closings,
  area,
}: {
  column: EntityTableColumn;
  config: MultiSelectConfig;
  debounceMs: number;
  closings: number;
  area: Area;
}) => {
  const { t } = useTranslation();
  const selectedValues = config.selectedValues || [];
  const search = useOptionSearch(config.options, config.onInputChange, debounceMs, closings);
  const isChosen = (option: SelectorValue) => selectedValues.some((selected) => selected.value === option.value);
  const chosenOutsideTheOptions = selectedValues.filter(
    (selected) => !search.matching.some((option) => option.value === selected.value),
  );
  const listed = [...(search.term ? [] : chosenOutsideTheOptions), ...search.matching];
  const loadMoreNearEnd = useLoadMoreNearEnd(config);

  const handleChange = (option: SelectorValue, isChecked: boolean) => {
    const values = isChecked ? [...selectedValues, option] : selectedValues.filter((v) => v.value !== option.value);
    config.onSelect(values);
    keepFocusIn(area, `[data-option-value="${CSS.escape(option.value)}"] input`);
  };

  return (
    <Box px={3} py={2} borderBottom="1px solid" borderColor="smBorder">
      {search.isShown && <OptionSearch term={search.term} onChange={search.change} />}
      <Box maxHeight="48" overflowY="auto" onScroll={loadMoreNearEnd} data-testid="value-list">
        {listed.length > 0 ? (
          listed.map((option) => (
            <Flex key={option.value} alignItems="center" data-option-value={option.value}>
              <Checkbox
                isChecked={isChosen(option)}
                onChange={(e) => handleChange(option, e.target.checked)}
                size="sm"
                spacing={2}
              >
                <Text
                  as="span"
                  display="block"
                  fontSize="sm"
                  maxWidth="60"
                  whiteSpace="nowrap"
                  overflowX="hidden"
                  overflowY="hidden"
                  textOverflow="ellipsis"
                  textTransform="none"
                >
                  {option.label}
                </Text>
              </Checkbox>
            </Flex>
          ))
        ) : column.multiSelectConfig?.isLoading ||
          column.multiSelectConfig?.selectedValuesLoading ||
          config.isError ? null : (
          <Text fontSize="sm" color="smMuted" textTransform="none" fontWeight="normal" p={2}>
            {t("No options found")}...
          </Text>
        )}
      </Box>
      {config.isLoading || config.selectedValuesLoading ? (
        <Flex width="full" justifyContent="center" pt="2">
          <Spinner size="sm" color="smMuted" />
        </Flex>
      ) : config.isError ? (
        <OptionsLoadError onRetry={config.onRetry} />
      ) : config.optionsCount ? (
        <Flex justifyContent="center" pt="1">
          <Text fontSize="xs" color="smMuted" fontWeight="normal" textTransform="none">
            {config.options.length} {t("out of")} {config.optionsCount}
          </Text>
        </Flex>
      ) : null}
    </Box>
  );
};

const SingleSelect = ({
  config,
  debounceMs,
  closings,
  area,
}: {
  config: SingleSelectConfig;
  debounceMs: number;
  closings: number;
  area: Area;
}) => {
  const { t } = useTranslation();
  const selectedValue = config.selectedValue || null;
  const search = useOptionSearch(config.options, config.onInputChange, debounceMs, closings);
  const loadMoreNearEnd = useLoadMoreNearEnd(config);

  const handleChange = (optionValue: string) => {
    config.onSelect(config.options.find((opt) => opt.value === optionValue) ?? { value: "", label: "" });
    keepFocusIn(area, null);
  };

  return (
    <Box px={3} py={2}>
      <Text fontSize="sm" fontWeight="medium" mb={2} color="smMuted">
        {t("Select value")}
      </Text>
      {search.isShown && <OptionSearch term={search.term} onChange={search.change} />}
      <RadioGroup value={selectedValue?.value || ""} onChange={handleChange}>
        <Box maxHeight="48" overflowY="auto" onScroll={loadMoreNearEnd} data-testid="value-list">
          <Box pb={0}>
            <Radio value="" size="sm">
              <Text fontSize="sm" textTransform="none">
                {t("All")}
              </Text>
            </Radio>
          </Box>
          {search.matching.length > 0 ? (
            search.matching.map((option) => (
              <Box key={option.value} pb={0}>
                <Radio value={option.value} size="sm">
                  <Text fontSize="sm" textTransform="none">
                    {option.label}
                  </Text>
                </Radio>
              </Box>
            ))
          ) : config.isError ? null : (
            <Text fontSize="sm" color="smMuted" textTransform="none" fontWeight="normal" p={2}>
              {t("No options found")}...
            </Text>
          )}
        </Box>
      </RadioGroup>
      {config.isLoading ? (
        <Flex width="full" justifyContent="center" pt="2">
          <Spinner size="sm" color="smMuted" />
        </Flex>
      ) : config.isError ? (
        <OptionsLoadError onRetry={config.onRetry} />
      ) : config.optionsCount ? (
        <Text fontSize="xs" color="smMuted" fontWeight="normal" mb={2} textTransform="none">
          {config.options.length} {t("out of")} {config.optionsCount}
        </Text>
      ) : null}
    </Box>
  );
};

const DateRange = ({ config }: { config: DateRangeConfig }) => {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = useState(config.fromDate || "");
  const [toDate, setToDate] = useState(config.toDate || "");

  useEffect(() => {
    setFromDate(config.fromDate || "");
  }, [config.fromDate]);

  useEffect(() => {
    setToDate(config.toDate || "");
  }, [config.toDate]);

  const handleFromDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFromDate(e.target.value);
    config.onFromDateChange(e.target.value || undefined);
  };

  const handleToDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    setToDate(e.target.value);
    config.onToDateChange(e.target.value || undefined);
  };

  const handlePresetChange = (preset: string) => {
    setFromDate("");
    setToDate("");
    config.onPresetChange?.(preset);
  };

  return (
    <Box px={3} py={2} borderBottom="1px solid" borderColor="smBorder">
      <Text fontSize="sm" fontWeight="medium" mb={2} color="smMuted">
        {t("Date range")}
      </Text>
      {config.presets && config.presets.length > 0 && (
        <RadioGroup value={config.preset ?? ""} onChange={handlePresetChange} mb={2}>
          <Stack spacing={1}>
            {config.presets.map((preset) => (
              <Radio key={preset.value} value={preset.value} size="sm">
                <Text fontSize="sm" textTransform="none">
                  {preset.label}
                </Text>
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      )}
      <Flex gap={2}>
        <Input
          type="date"
          size="sm"
          placeholder={config.fromDateLabel || String(t("From"))}
          aria-label={config.fromDateLabel || String(t("From"))}
          value={fromDate || ""}
          onChange={handleFromDateChange}
        />
        <Input
          type="date"
          size="sm"
          placeholder={config.toDateLabel || String(t("To"))}
          aria-label={config.toDateLabel || String(t("To"))}
          value={toDate || ""}
          onChange={handleToDateChange}
        />
      </Flex>
    </Box>
  );
};
